import axios from '../utils/axios';

export interface UserModulePermissions {
  isAccountOwner: boolean;
  // Admin-level access overlay granted by an Admin. Treated exactly like account
  // ownership for access purposes, but the user's role (baseRole) is unchanged.
  hasFullAccess?: boolean;
  baseRole: string | null;
  moduleAccess: Record<string, {
    permissions: string[];
    scope: string;
    subModules?: Record<string, { permissions: string[]; scope: string }>;
  }>;
  // Whether this account enforces module/location access (opt-in). When false,
  // nothing is hidden/blocked on the frontend (behaviour unchanged).
  permissionEnforcement?: boolean;
  contextPermissions: {
    isManager?: boolean;
    canApproveLeaves?: boolean;
    canApproveAttendance?: boolean;
    canApproveTimesheets?: boolean;
    canApproveExpenses?: boolean;
    canAssignRoles?: boolean;
    approvalScope?: string;
  };
  effectivePermissions: string[];
  canApprove: {
    leaves: boolean;
    attendance: boolean;
    timesheets: boolean;
    expenses: boolean;
    scope: string;
  };
  isManager: boolean;
}

/**
 * Service to fetch user permissions from the new module access system
 */
class ModulePermissionService {
  
  /**
   * Get user permissions from the new module access system
   */
  async getUserModulePermissions(userId: string): Promise<UserModulePermissions> {
    try {
      const response = await axios.get(`/api/v1/permissions/user/${userId}`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching module permissions:', error);
      throw error;
    }
  }

  /**
   * Convert new module permissions to the old format for compatibility
   */
  convertToLegacyFormat(modulePermissions: UserModulePermissions): string[] {
    const legacyPermissions: string[] = [];
    
    // Debug logging
    console.log('convertToLegacyFormat - input modulePermissions:', modulePermissions);
    
    // Account owner — or a user holding the admin-level access overlay — gets
    // every legacy permission.
    if (modulePermissions.isAccountOwner || modulePermissions.hasFullAccess) {
      // Add all possible permissions for backward compatibility
      const allPermissions = [
        'Users_View', 'Users_Create', 'Users_Update', 'Users_Delete',
        'Invoice_View', 'Invoice_Create', 'Invoice_Update', 'Invoice_Delete', 'Invoice_Send',
        'Customer_View', 'Customer_Create', 'Customer_Update', 'Customer_Delete',
        'Supplier_View', 'Supplier_Create', 'Supplier_Update', 'Supplier_Delete',
        'Employee_View', 'Employee_Create', 'Employee_Update', 'Employee_Delete',
        'Payment_View', 'Payment_Create', 'Payment_Update', 'Payment_Delete',
        'Reports_View', 'Reports_Export',
        'Dashboard_View',
        'Settings_View', 'Settings_Update',
        'Cashflow_View', 'Cashflow_Manage',
        'Inventory_View', 'Inventory_Create', 'Inventory_Update', 'Inventory_Delete',
        'Account_View', 'Account_Create', 'Account_Update', 'Account_Delete',
        'Lead_View', 'Lead_Create', 'Lead_Update', 'Lead_Delete', 'Lead_Convert',
        'Campaign_View', 'Campaign_Create', 'Campaign_Update', 'Campaign_Delete',
        'TelecallingLog_View', 'TelecallingLog_Create', 'TelecallingLog_Update', 'TelecallingLog_Delete',
        'FollowUp_View', 'FollowUp_Create', 'FollowUp_Update', 'FollowUp_Delete',
        'WebhookIngestion_View', 'WebhookIngestion_Update'
      ];
      return allPermissions;
    }

    // Convert module access to legacy format
    Object.entries(modulePermissions.moduleAccess).forEach(([module, access]) => {
      access.permissions.forEach(permission => {
        // Map new permission format to legacy format
        const legacyPermission = this.mapToLegacyPermission(module, permission);
        if (legacyPermission && !legacyPermissions.includes(legacyPermission)) {
          legacyPermissions.push(legacyPermission);
        }
      });
    });

    // Add context-based permissions
    if (modulePermissions.moduleAccess.members?.permissions?.includes('assign-roles')) {
      legacyPermissions.push('Users_Update'); // Needed to manage user roles
    }

    console.log('convertToLegacyFormat - output legacyPermissions:', legacyPermissions);
    return legacyPermissions;
  }

  /**
   * Map new module/permission format to legacy permission format
   */
  private mapToLegacyPermission(module: string, permission: string): string | null {
    const mapping: Record<string, Record<string, string>> = {
      'business': {
        'view': 'Account_View',
        'create': 'Account_Create',
        'edit': 'Account_Update',
        'delete': 'Account_Delete'
      },
      'dashboard': {
        'view': 'Dashboard_View'
      },
      'sales': {
        'view': 'Invoice_View',
        'create': 'Invoice_Create',
        'edit': 'Invoice_Update',
        'delete': 'Invoice_Delete',
        'send': 'Invoice_Send'
      },
      'purchase': {
        'view': 'Supplier_View',
        'create': 'Supplier_Create',
        'edit': 'Supplier_Update',
        'delete': 'Supplier_Delete'
      },
      'finance': {
        'view': 'Payment_View',
        'create': 'Payment_Create',
        'edit': 'Payment_Update',
        'delete': 'Payment_Delete',
        'export': 'Reports_Export'
      },
      'hr': {
        'view': 'Employee_View',
        'create': 'Employee_Create',
        'edit': 'Employee_Update',
        'delete': 'Employee_Delete'
      },
      'inventory': {
        'view': 'Inventory_View',
        'create': 'Inventory_Create',
        'edit': 'Inventory_Update',
        'delete': 'Inventory_Delete'
      },
      'reports': {
        'view': 'Reports_View',
        'export': 'Reports_Export'
      },
      'attendance': {
        'view': 'Employee_View', // Reuse employee permissions for attendance
        'approve': 'Employee_Update'
      },
      'leaves': {
        'view': 'Employee_View', // Reuse employee permissions for leaves
        'approve': 'Employee_Update'
      },
      'accounting': {
        'view': 'Account_View',
        'create': 'Account_Create',
        'edit': 'Account_Update',
        'delete': 'Account_Delete'
      },
      'crm': {
        'view': 'Lead_View',
        'create': 'Lead_Create',
        'edit': 'Lead_Update',
        'delete': 'Lead_Delete',
        'convert': 'Lead_Convert'
      }
    };

    // Add customer permissions for sales module
    if (module === 'sales') {
      const customerMapping = {
        'view': 'Customer_View',
        'create': 'Customer_Create',
        'edit': 'Customer_Update',
        'delete': 'Customer_Delete'
      };
      const customerPermission = customerMapping[permission as keyof typeof customerMapping];
      if (customerPermission) {
        return customerPermission;
      }
    }

    return mapping[module]?.[permission] || null;
  }

  /**
   * Get sidebar categories based on module permissions
   */
  getSidebarCategories(modulePermissions: UserModulePermissions): Record<string, any[]> {
    const categories: Record<string, any[]> = {};

    // Debug logging
    console.log('getSidebarCategories - isAccountOwner:', modulePermissions.isAccountOwner);
    console.log('getSidebarCategories - moduleAccess:', modulePermissions.moduleAccess);

    // Account owner / admin-level access — show every category.
    if (modulePermissions.isAccountOwner || modulePermissions.hasFullAccess) {
      return {
        'Sales': [
          { resource: 'Invoice', displayName: 'Invoices', category: 'Sales', to: '/sales', path: 'sales' },
          { resource: 'Customer', displayName: 'Buyers', category: 'Sales', to: '/buyers', path: 'buyers' }
        ],
        'Purchase': [
          { resource: 'Invoice', displayName: 'Invoices', category: 'Purchase', to: '/purchases', path: 'purchases' },
          { resource: 'Supplier', displayName: 'Suppliers', category: 'Purchase', to: '/suppliers', path: 'suppliers' }
        ],
        'Finance': [
          // { resource: 'Payment', displayName: 'Chart of Accounts', category: 'Finance', to: '/coa', path: 'coa' },
          { resource: 'Payment', displayName: 'Payments', category: 'Finance', to: '/payments', path: 'payments' }
        ],
        'HR': [
          { resource: 'Employee', displayName: 'Employees', category: 'HR', to: '/people/employee', path: 'people/employee' }
        ],
        'Operations': [
          { resource: 'Inventory', displayName: 'Inventory', category: 'Operations', to: '/inventory', path: 'inventory' }
        ],
        'Analytics': [
          { resource: 'Reports', displayName: 'Reports', category: 'Analytics', to: '/reports', path: 'reports' }
        ],
        'CRM': [
          { resource: 'Lead', displayName: 'Leads', category: 'CRM', to: '/crm/leads', path: 'crm/leads' },
          { resource: 'Campaign', displayName: 'Campaigns', category: 'CRM', to: '/crm/campaigns', path: 'crm/campaigns' },
          { resource: 'TelecallingLog', displayName: 'Telecalling', category: 'CRM', to: '/crm/telecalling', path: 'crm/telecalling' },
          { resource: 'FollowUp', displayName: 'Follow-ups', category: 'CRM', to: '/crm/follow-ups', path: 'crm/follow-ups' }
        ],
        'Administrative': [
          { resource: 'Users', displayName: 'Users', category: 'Administrative', to: '/users', path: 'users' },
          // { resource: 'Account', displayName: 'Accounts', category: 'Administrative', to: '/account', path: 'account' }
        ]
      };
    }

    // Build categories based on module access
    Object.entries(modulePermissions.moduleAccess).forEach(([module, access]) => {
      if (access.permissions.includes('view')) {
        switch (module) {
          case 'sales':
            categories['Sales'] = categories['Sales'] || [];
            categories['Sales'].push(
              { resource: 'Invoice', displayName: 'Sales', category: 'Sales', to: '/sales', path: 'purchases' },
              { resource: 'Customer', displayName: 'Customers', category: 'Sales', to: '/customer', path: 'purchases' }
            );
            break;
          case 'purchase':
            categories['Purchase'] = categories['Purchase'] || [];
            categories['Purchase'].push(
              { resource: 'Invoice', displayName: 'Purchase', category: 'Purchase', to: '/purchases', path: 'purchases' },
              { resource: 'Supplier', displayName: 'Suppliers', category: 'Purchase', to: '/supplier', path: 'purchases' }
            );
            break;
          case 'finance':
            categories['Finance'] = categories['Finance'] || [];
            categories['Finance'].push(
              { resource: 'Payment', displayName: 'Payments', category: 'Finance', to: '/payment', path: 'payment' }
            );
            break;
          case 'hr':
            categories['HR'] = categories['HR'] || [];
            categories['HR'].push(
              { resource: 'Employee', displayName: 'Employees', category: 'HR', to: '/people/employee', path: 'people/employee' }
            );
            break;
          case 'inventory':
            categories['Operations'] = categories['Operations'] || [];
            categories['Operations'].push(
              { resource: 'Inventory', displayName: 'Inventory', category: 'Operations', to: '/inventory', path: 'inventory' }
            );
            break;
          case 'reports':
            categories['Analytics'] = categories['Analytics'] || [];
            categories['Analytics'].push(
              { resource: 'Reports', displayName: 'Reports', category: 'Analytics', to: '/reports', path: 'reports' }
            );
            break;
          case 'crm':
            categories['CRM'] = categories['CRM'] || [];
            categories['CRM'].push(
              { resource: 'Lead', displayName: 'Leads', category: 'CRM', to: '/crm/leads', path: 'crm/leads' },
              { resource: 'Campaign', displayName: 'Campaigns', category: 'CRM', to: '/crm/campaigns', path: 'crm/campaigns' },
              { resource: 'TelecallingLog', displayName: 'Telecalling', category: 'CRM', to: '/crm/telecalling', path: 'crm/telecalling' },
              { resource: 'FollowUp', displayName: 'Follow-ups', category: 'CRM', to: '/crm/follow-ups', path: 'crm/follow-ups' }
            );
            break;
        }
      }
    });

    // Always add Users if they can assign roles or if they're a manager
    if (modulePermissions.moduleAccess.members?.permissions?.includes('assign-roles') || modulePermissions.isManager) {
      categories['Administrative'] = categories['Administrative'] || [];
      categories['Administrative'].push(
        { resource: 'Users', displayName: 'Users', category: 'Administrative', to: '/users', path: 'users' }
      );
    }

    console.log('getSidebarCategories - output categories:', categories);
    return categories;
  }
}

export const modulePermissionService = new ModulePermissionService();
export default modulePermissionService;